import { redirect } from 'next/navigation';
import {
  getLatestOrgId,
  getLatestWorkspaceId,
  getOrganizations,
  getUser,
  getWorkspaces,
} from '@nx-frontend/shared/api/webnotes';
import { User } from '@nx-frontend/shared/types';
import { Workspace } from '@nx-frontend/shared/types';
import { Org } from '@nx-frontend/shared/types';

export async function getWorkspaceBySessionId(sessionId: string) {
  let user: User | null = null;

  user = await getUser(sessionId);
  if (!user || !user.id) {
    redirect('/auth');
    return null;
  }

  const payload = { userId: user.id };
  const [organizationsResponse, workspacesResponse] = await Promise.all(
    [getOrganizations(payload), getWorkspaces(payload)].map((promise) =>
      promise.catch((error) => {
        return null;
      })
    )
  );

  let organizations = organizationsResponse as Org[];
  if (organizations && organizations.length) {
    organizations = organizations.filter(({ type }) => type === 'notes');
  }

  let workspaces: Workspace[] | null = [];
  workspaces = workspacesResponse as Workspace[];

  let defaultOrg: Org | undefined = undefined;
  defaultOrg = organizations.find((organization) => {
    return user && organization.userId === user.id && organization.globalId.charAt(0) === 'u';
  });
  let orgId = defaultOrg ? defaultOrg.globalId : '';
  const initialOrgId = orgId;

  let latestOrgId: string | null;
  try {
    latestOrgId = await getLatestOrgId({ userId: user.id });
  } catch (err) {
    latestOrgId = null;
  }
  if (latestOrgId) {
    orgId = latestOrgId;
  } else {
    const memberInOrgs = organizations.filter((org) => org.userId !== user?.id && !org.suspended);
    if (memberInOrgs.length) {
      orgId = memberInOrgs[0].globalId;
    }
  }

  let latestWorkspaceId = await getLatestWorkspaceId({ userId: user.id, orgId });
  if (latestWorkspaceId) {
    const workspaceFound = workspaces.find((workspace) => {
      return workspace.globalId === latestWorkspaceId;
    });
    if (!workspaceFound) {
      latestWorkspaceId = null;
    }
  }
  let workspaceId = latestWorkspaceId;

  if (!workspaceId) {
    const lastOpenedWorkspace = workspaces.find((workspace) => {
      return workspace.isDefault && workspace.orgId === orgId;
    });
    if (lastOpenedWorkspace) {
      workspaceId = lastOpenedWorkspace.globalId;
    } else if (initialOrgId) {
      orgId = initialOrgId;
      const defaultWorkspace = workspaces.find((workspace) => {
        return workspace.isDefault && workspace.orgId === orgId;
      });
      if (defaultWorkspace) {
        workspaceId = defaultWorkspace.globalId;
      }
    }
  }
  if (!workspaceId) {
    const firstWorkspace = workspaces.find((workspace) => {
      return workspace.orgId === orgId;
    });
    if (firstWorkspace) {
      workspaceId = firstWorkspace.globalId;
    }
  }

  return workspaceId;
}
